'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { Focusable } from '@ariakit/react';
import styled, { useTheme } from 'styled-components';
import { fromMd, timingDefault, withHoverSupport } from '../../constants';
import { focusOutline } from '../../utility-classes';
import { useLmfAnalyticsOnClick } from '../Analytics/LmfAnalyticsProvider';
import { TypeStyle } from '../TypeStyle';
import { extractButtonColorsByVariant } from './utils';
export function buttonSizeToHeight(size) {
    const mapping = {
        large: 50,
        medium: 42,
        small: 32,
    };
    return `${mapping[size]}px`;
}
export const StyledButton = styled.button `
  ${TypeStyle.css.eyebrow};
  ${focusOutline};

  ${({ $allCaps = true, $block = false, $size = 'medium', $borderless = false, $flushLeft, $flushRight, $variantColors, $callToActionColor, $noWrap, ...rest }) => {
    const isDisabled = rest.disabled || rest['aria-disabled'];
    const { normal: normalColors, hover: defaultHoverColors, disabled: disabledColors, } = $variantColors;
    const colors = isDisabled
        ? disabledColors
        : $callToActionColor
            ? {
                borderColor: $callToActionColor,
                backgroundColor: $callToActionColor,
                color: rest.theme.color.button.primary.content.on.background,
            }
            : normalColors;
    const hoverColors = $callToActionColor
        ? {
            borderColor: applyHoverToAccentColor($callToActionColor),
            backgroundColor: applyHoverToAccentColor($callToActionColor),
            color: rest.theme.color.button.primary.content.on.background,
        }
        : defaultHoverColors;
    return `
      align-items: center;
      background-color: ${colors.backgroundColor};
      border-color: ${$borderless ? 'transparent' : colors.borderColor};
      border-radius: 4px;
      border-style: solid;
      border-width: 2px;
      color: ${colors.color};
      cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
      display: ${$block ? 'flex' : 'inline-flex'};
      font-size: ${$size === 'small' ? '11px' : '12px'};
      justify-content: center;
      min-height: ${buttonSizeToHeight($size)};
      padding: 0 32px;
      ${$flushLeft ? 'margin-left: -32px;' : null};
      ${$flushRight ? 'margin-right: -32px;' : null};
      white-space: ${$noWrap ? 'nowrap' : 'normal'};
      text-transform: ${$allCaps ? 'uppercase' : null};
      transition:
        background-color ${timingDefault},
        border-color ${timingDefault},
        color ${timingDefault};
      ${$block
        ? `
        width: 100%;
        width: -moz-available;
        width: -webkit-fill-available;
        width: fill-available;
        `
        : null};
      position: relative;

      ${fromMd} {
        font-size: 12px;
      }

      ${withHoverSupport} {
        &:hover {
          background-color: ${!isDisabled ? hoverColors.backgroundColor : null};
          border-color: ${isDisabled || $borderless ? 'transparent' : hoverColors.borderColor};
          color: ${!isDisabled ? hoverColors.color : null};
        }
      }
    `;
}}
`;
export const Button = ({ allCaps, 'aria-disabled': ariaDisabled = false, accessibleWhenDisabled = true, block, disabled = false, size, variant = 'filled', borderless, flushBottom, flushLeft, flushRight, flushTop, customVariants, callToActionColor, noWrap, onClick, ref, ...rest }) => {
    const theme = useTheme();
    const variantColors = extractButtonColorsByVariant({
        theme,
        variant,
        customVariants,
    });
    const onClickWithTracking = useLmfAnalyticsOnClick({
        element: 'button',
        variations: [String(variant)],
        label: rest['aria-label'],
        href: rest.href,
        onClick: onClick,
    });
    return (_jsx(Focusable, { render: _jsx(StyledButton, { ...rest, onClick: onClickWithTracking, ref: ref, "$callToActionColor": callToActionColor, "$allCaps": allCaps, "$block": block, "$size": size, "$variant": variant, "$borderless": borderless, "$flushBottom": flushBottom, "$flushLeft": flushLeft, "$flushRight": flushRight, "$flushTop": flushTop, "$variantColors": variantColors, "$noWrap": noWrap }), disabled: Boolean(disabled || ariaDisabled), accessibleWhenDisabled: accessibleWhenDisabled }));
};
const applyHoverToAccentColor = (x) => /^#.{6}$/.test(x?.toString() ?? '') ? `${x}d9` : x;
//# sourceMappingURL=Button.js.map